import { useMemo } from "react";
import { Link as RouterLink } from "react-router";
import { Box, Link, Typography } from "@mui/material";
import { format, parseJSON } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { TransactionV2 } from "@rentablo/api/apiv1";
import { toCurrency } from "@helpers";
import { GainLossTypography, TableConfig } from "@rentablo/components";
import { TransactionStatusIcon } from "@features/investments/investment/components/TransactionStatusIcon";
import { IntervalInfo } from "./IntervalInfo";

export type AccountProtocolTableAdditionalProps = {
  currency?: string;
};

type RendererProps = {
  row: TransactionV2;
} & AccountProtocolTableAdditionalProps;

const PurposeText = styled(Typography)`
  word-break: break-all;
`;

const ValueDateCell = ({ row }: RendererProps) => (
  <>
    <Box display="flex" alignItems="center" gap={2}>
      {row.booking && (
        <TransactionStatusIcon
          dataConsistencyStatus={row.booking?.dataConsistencyStatus}
          dataSource={row.booking?.dataSource}
          isExternalID={Boolean(row.booking?.externalId)}
        />
      )}
      <Typography variant="body1" color="textSecondary">
        {format(parseJSON(row.valueDate!), "dd.MM.yyyy")}
      </Typography>
    </Box>
    <IntervalInfo
      isRegular={row.isRegular}
      paymentInterval={row.paymentInterval}
      isInterAccountTransfer={row.isInterAccountTransfer}
      isAdjustingEntry={row.isAdjustingEntry}
    />
  </>
);

const BookingInfoCell = ({ row }: RendererProps) => {
  const { t } = useTranslation();

  if (row.isAdjustingEntry) {
    return (
      <Typography variant="body1" color="textSecondary">
        {t("singleAccountPage.protocolTable.adjustingEntryText")}
      </Typography>
    );
  }

  return (
    <>
      {row.booking && (
        <Link
          variant="body1"
          component={RouterLink}
          to={`/investments/${row.booking.investment.id}`}
        >
          {row.booking.investment.name}
        </Link>
      )}
      <PurposeText variant="body1" color="textSecondary">
        {row.purpose || t("singleAccountPage.protocolTable.noPurpose")}{" "}
        {row.literalType && `(Typ: ${row.literalType})`}
      </PurposeText>
    </>
  );
};

const ValueCell = ({ row, currency = "EUR" }: RendererProps) => (
  <Box display="flex" justifyContent="end">
    {typeof row.amount === "number" ? (
      <GainLossTypography variant="subtitle1" noWrap value={row.amount}>
        {toCurrency(row.amount, currency)}
      </GainLossTypography>
    ) : (
      "N/A"
    )}
  </Box>
);

type SortArgs = {
  sortField?: string;
  sortDirection?: TableSortDirection;
  onSort: (field: string) => void;
};

export const useAccountProtocolTableConfig = ({
  sortField,
  sortDirection,
  onSort,
}: SortArgs): TableConfig<TransactionV2> => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      id: "account-protocol-table",
      getRowId: (row) => row.id,
      sorting:
        sortField && sortDirection
          ? { field: sortField, direction: sortDirection }
          : undefined,
      onSort,
      columns: [
        {
          id: "valueDate",
          sortKey: "valueDate",
          title: t("singleAccountPage.protocolTable.headers.valueDate"),
          sortable: true,
          renderer: ValueDateCell,
        },
        {
          id: "bookingInformation",
          sortKey: "purpose",
          title: t(
            "singleAccountPage.protocolTable.headers.bookingInformation"
          ),
          sortable: true,
          renderer: BookingInfoCell,
        },
        {
          id: "value",
          sortKey: "amount",
          title: t("singleAccountPage.protocolTable.headers.value"),
          sortable: true,
          align: "right",
          renderer: ValueCell,
        },
      ],
    }),
    [t, sortField, sortDirection, onSort]
  );
};
